import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { spotlightModifiersAtom } from '@frontend/components/Spotlight/store';

// Frozen: this shared default is written straight into the atom, so a stray
// in-place mutation anywhere must not be able to corrupt it.
const NONE = Object.freeze({ cmd: false, ctrl: false, shift: false, opt: false });

/**
 * Track live modifier-key state into `spotlightModifiersAtom` while `active`
 * (i.e. the Spotlight is open), for render hints. Resets on window blur so a
 * modifier released outside the window can't get stuck, and clears when
 * inactive. Execute paths read modifiers from the triggering event instead, so
 * this is purely for display.
 */
export function useSpotlightModifiers(active: boolean): void {
  const setModifiers = useSetAtom(spotlightModifiersAtom);

  useEffect(() => {
    if (!active) {
      setModifiers(NONE);
      return;
    }
    // Skip writing a fresh object when the flags are unchanged — a plain keystroke
    // (typing, arrows) would otherwise re-derive the whole Spotlight view.
    const read = (e: KeyboardEvent) =>
      setModifiers(prev =>
        prev.cmd === e.metaKey &&
        prev.ctrl === e.ctrlKey &&
        prev.shift === e.shiftKey &&
        prev.opt === e.altKey
          ? prev
          : { cmd: e.metaKey, ctrl: e.ctrlKey, shift: e.shiftKey, opt: e.altKey }
      );
    const reset = () => setModifiers(NONE);

    window.addEventListener('keydown', read);
    window.addEventListener('keyup', read);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', read);
      window.removeEventListener('keyup', read);
      window.removeEventListener('blur', reset);
      setModifiers(NONE);
    };
  }, [active, setModifiers]);
}
